import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { WorkImage, WorkTarget } from "../lib/api-client/workReports";
import { ImageViewer, ImageViewerContent, ImageViewerThumbnail } from "./beui/ImageViewer";

/** Private originals are scoped to this mounted record/profile. Abort reads and revoke Blob URLs on scope or access changes. */
export function WorkReportImages({ target, images, author, onAccessLost }: {
  target: WorkTarget; images: WorkImage[]; author: string; onAccessLost: (failure: unknown) => boolean;
}) {
  const [loaded, setLoaded] = useState<Record<string, { src: string; width: number; height: number }>>({});
  const [failed, setFailed] = useState<string[]>([]);
  const [retry, setRetry] = useState(0), [expanded, setExpanded] = useState<string | null>(null);
  const reject = useRef(onAccessLost); reject.current = onAccessLost;
  useEffect(() => {
    const abort = new AbortController(), urls: string[] = [];
    setLoaded({}); setFailed([]); setExpanded(null);
    for (const image of images) {
      void api.workImage(target, image.id, abort.signal).then(blob => {
        if (abort.signal.aborted) return;
        const url = URL.createObjectURL(blob); urls.push(url);
        const decoded = new Image();
        decoded.onload = () => { if (!abort.signal.aborted) setLoaded(current => ({ ...current, [image.id]: { src: url, width: decoded.naturalWidth, height: decoded.naturalHeight } })); };
        decoded.onerror = () => { if (!abort.signal.aborted) setFailed(current => [...current, image.id]); };
        decoded.src = url;
      }).catch((failure: unknown) => {
        if (!abort.signal.aborted && !reject.current(failure)) setFailed(current => [...current, image.id]);
      });
    }
    return () => { abort.abort(); urls.forEach(url => URL.revokeObjectURL(url)); };
  }, [target, images, retry]);
  const viewerImages = images.filter(image => loaded[image.id]).map(image => ({ id: image.id, ...loaded[image.id], alt: `Work image ${image.position + 1} from ${author}` }));
  return <ImageViewer images={viewerImages} value={expanded} onValueChange={setExpanded} label={`Work images from ${author}`}><div className="wu-report-images">
    {images.map(image => <div key={image.id} className="wu-report-image">
      {loaded[image.id] ? <ImageViewerThumbnail imageId={image.id} aria-label={`View work image ${image.position + 1} from ${author}`} className="block w-full rounded-lg focus-visible:ring-2 focus-visible:ring-navy"
        imageClassName="h-full w-full object-cover" /> : <div className="flex min-h-24 flex-col justify-center rounded-lg bg-navy-50 p-2 text-xs text-mut">
        {failed.includes(image.id) ? <><p>Could not load image.</p><button type="button" className="mt-1 min-h-11 text-navy underline" onClick={() => setRetry(current => current + 1)}>Retry image</button></>
          : <span role="status">Loading image…</span>}
      </div>}
    </div>)}
  </div><ImageViewerContent /></ImageViewer>;
}
